import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Stack,
  Chip
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { salespersonApi, Salesperson } from '../../services/salespersonApi';


const SalespersonSettingsPage: React.FC = () => {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const { data, isLoading, isError } = useQuery<Salesperson[]>({
    queryKey: ['salespersons'],
    queryFn: () => salespersonApi.getSalespersons('TR'),
  });

  const list = React.useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(x =>
      x.salespersonCode?.toLowerCase().includes(q) ||
      x.salespersonFirstLastName?.toLowerCase().includes(q) ||
      x.officeCode?.toLowerCase().includes(q) ||
      x.companyCode?.toLowerCase().includes(q)
    );
  }, [data, search]);

  // Sayfalama için dilimlenmiş liste
  const pagedList = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return list.slice(start, end);
  }, [list, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Satış Sorumluları
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Ara: kod, ad, ofis, şirket"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Stack direction="row" spacing={2} mt={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Toplam: {list.length} kayıt
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 0 }}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Kod</TableCell>
                <TableCell>Ad Soyad</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Takım</TableCell>
                <TableCell>Şirket</TableCell>
                <TableCell>Ofis</TableCell>
                <TableCell>Perakende</TableCell>
                <TableCell>Toptan</TableCell>
                <TableCell>Bloke</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError && (
                <TableRow>
                  <TableCell colSpan={9}>Liste alınamadı.</TableCell>
                </TableRow>
              )}
              {!isLoading && list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>Kayıt bulunamadı.</TableCell>
                </TableRow>
              )}
              {pagedList.map((sp) => (
                <TableRow key={sp.salespersonCode} hover>
                  <TableCell>{sp.salespersonCode}</TableCell>
                  <TableCell>{sp.salespersonFirstLastName}</TableCell>
                  <TableCell>{sp.salespersonType}</TableCell>
                  <TableCell>{sp.salespersonTeam}</TableCell>
                  <TableCell>{sp.companyCode} - {sp.companyName}</TableCell>
                  <TableCell>{sp.officeCode} - {sp.officeDescription}</TableCell>
                  <TableCell>
                    <Chip size="small" label={sp.isWorkOnRetail ? 'Evet' : 'Hayır'} color={sp.isWorkOnRetail ? 'success' : 'default'} variant={sp.isWorkOnRetail ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={sp.isWorkOnWholesale ? 'Evet' : 'Hayır'} color={sp.isWorkOnWholesale ? 'success' : 'default'} variant={sp.isWorkOnWholesale ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={sp.isBlocked ? 'Evet' : 'Hayır'} color={sp.isBlocked ? 'error' : 'success'} variant={sp.isBlocked ? 'filled' : 'outlined'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={list.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sayfa başına"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `>${to}`}`}
        />
      </Paper>
    </Box>
  );
};

export default SalespersonSettingsPage;
